import styled from 'styled-components'

import { _M_Bold, _S_Normal, _XS_Medium, _XS_Normal, mainColors } from '@avantstay/backoffice-core'

// Covers the chat area inside the panel (S.Root is the positioning context),
// so the guide never fights AttachmentBar's portal/z-index stack.
export const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 15;
  display: flex;
  flex-direction: column;
  background: ${mainColors.white};
`

// Mirrors RemyChat's ChatToolbar metrics (padding + button height), so the
// header doesn't jump when switching between chat and guide.
export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 8px 16px;
  border-bottom: 1px solid ${mainColors.neutralMedium};
  background: ${mainColors.neutralExtraLight};
`

export const Title = styled.h3`
  ${_M_Bold}
  margin: 0;
  color: ${mainColors.neutralExtremelyDark2};
`

// Text action instead of an X — the panel header already has a close icon,
// and two Xs stacked vertically read as duplicates.
export const BackButton = styled.button`
  ${_XS_Medium}
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 6px;
  border: 1px solid ${mainColors.neutralMedium};
  background: ${mainColors.white};
  color: ${mainColors.neutralExtremelyDark2};
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    border-color: ${mainColors.accentLight};
    color: ${mainColors.accentDark};
    background: ${mainColors.accentExtremelyLight};
  }

  svg {
    flex-shrink: 0;
  }
`

export const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px 32px;
`

export const Status = styled.p`
  ${_S_Normal}
  color: ${mainColors.neutralExtraDark};
`

// Typography for the rendered markdown. The guide is a long-form document, so
// unlike chat bubbles it keeps a real heading hierarchy.
export const MarkdownContent = styled.div`
  ${_S_Normal}
  color: ${mainColors.neutralExtremelyDark2};
  line-height: 1.6;

  & h1 {
    font-size: 20px;
    font-weight: 700;
    margin: 0 0 12px;
  }
  & h2 {
    font-size: 16px;
    font-weight: 700;
    margin: 24px 0 8px;
  }
  & h3 {
    font-size: 14px;
    font-weight: 700;
    margin: 18px 0 6px;
  }
  & p {
    margin: 0 0 10px;
  }
  & ul,
  & ol {
    margin: 0 0 10px;
    padding-left: 22px;
  }
  & li {
    margin-bottom: 4px;
  }
  & hr {
    border: none;
    border-top: 1px solid ${mainColors.neutralMedium};
    margin: 20px 0;
  }
  & code {
    ${_XS_Normal}
    font-family: ui-monospace, SFMono-Regular, monospace;
    background: ${mainColors.neutralLight};
    border-radius: 4px;
    padding: 1px 5px;
  }
  & pre {
    background: ${mainColors.neutralExtraLight};
    border: 1px solid ${mainColors.neutralMedium};
    border-radius: 6px;
    padding: 12px;
    overflow-x: auto;
    margin: 0 0 10px;

    & code {
      background: none;
      padding: 0;
    }
  }
  & table {
    border-collapse: collapse;
    margin: 0 0 12px;
    display: block;
    overflow-x: auto;
  }
  & th,
  & td {
    border: 1px solid ${mainColors.neutralMedium};
    padding: 6px 10px;
    text-align: left;
  }
  & th {
    background: ${mainColors.neutralExtraLight};
  }
  & a {
    color: ${mainColors.primaryMedium};
    text-decoration: none;
  }
  & a:hover {
    color: ${mainColors.primaryDark};
    text-decoration: underline;
  }
  & blockquote {
    margin: 0 0 10px;
    padding: 2px 14px;
    border-left: 3px solid ${mainColors.neutralMedium};
    color: ${mainColors.neutralExtraDark};
  }
`
